/**
 * The one id mint.
 *
 * Design §5.1 step 4 requires rows to be **built at the call site, before the transaction opens** —
 * the tap path pre-generates its id at `pointerdown`, and PRE-4's `CreatePlayer` has to know the id
 * its already-built `HypothesisRow` is bound to. So ids are minted by callers, and callers need one
 * function rather than one habit.
 *
 * ── `randomUUID`, where the design says "ULID" ───────────────────────────────
 *
 * §5.1 says "pre-generate the ULID". A ULID's distinguishing property is that its lexicographic
 * order is its creation order, and **nothing in this codebase depends on that**: `discoveryOrder`
 * and `suggestLockSelection` use `id asc` only as a final tiebreak to make an order *total*
 * (§6.1 F-11), never as a proxy for time; the archive sorts rows by id purely for determinism; and
 * §17.4's adversarial fixture deliberately carries "ids whose sort order is not their insertion
 * order", so any code that assumed otherwise is already broken by the fixture.
 *
 * Against that, `crypto.randomUUID` is a platform primitive available in every target runtime (iOS
 * Safari 17+, and `node:crypto`'s webcrypto under test — `app/writerLock.ts` already relies on it),
 * while a ULID would be a new runtime dependency where a platform primitive already does the job.
 * (Citation corrected 2026-08-19: this read *"a bundle ENV-4 wants small"*. ENV-4 has never had a
 * bundle-size clause in any revision — it was a plausible-sounding attribution to a real ID, which
 * is exactly how a citation escapes checking. The preference for a platform primitive over a new
 * dependency stands on its own.) Reported to the
 * orchestrator as a declared deviation rather than taken silently. Swapping it later is an edit to
 * this file and nothing else, which is why the mint is a function and not an inline call.
 */
export function newId(): string {
  return crypto.randomUUID();
}
